import { IUser, IRole } from '@/model/IUser';
import React, { useContext, useEffect, useState } from 'react';
import { ENVIRONMENT } from '@/environments/environment.ts';

const UserContext = React.createContext<{
  user: IUser;
  permissions: IRole[];
  setPermissions: any;
}>({
  user: null,
  permissions: [],
  setPermissions: null,
});

export function UserProvider({ user, children }) {
  const [permissions, setPermissions] = useState<IRole[]>([]);

  useEffect(() => {
    if (user && user.sysRoles) {
      setPermissions(user.sysRoles);
    }
  }, [user]);

  return (
    <UserContext.Provider value={{ user, permissions, setPermissions }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  return useContext(UserContext);
}

export function isUserInClaim(claim) {
  const ctx = useContext(UserContext);

  if (ctx && ctx.permissions) {
    const isAdmin = CheckIsAdmin() || CheckIsSuperAdmin();

    if (isAdmin) return true;

    const hasPermission = ctx.permissions.find(function (x) {
      return x.name == claim || x.systemName == claim;
    });
    return !!hasPermission;
  }
  return false;
}

export function userHasRoleOrClaims(requiredRoles: string[]): boolean {
  const ctx = useContext(UserContext);

  if (!ctx?.permissions)
    return false;

  return requiredRoles.some(role =>
    ctx.permissions.some(x => x.name === role || x.systemName === role)
  );
}

export function userHasRole(requiredRoles: string[]): boolean {
  const ctx = useContext(UserContext);

  if (!ctx?.permissions)
    return false;

  return requiredRoles.some(role =>
    ctx.permissions.some(x => x.name === role || x.systemName === role)
  );
}

export function IsBroker() {
  return isUserInClaim('BrokerUser');
}

export function CheckIsClient() {
  const ctx = useContext(UserContext);
  return ctx.user && ctx.user.isClient;
}

export function CheckIsExternalUser() {
  const ctx = useContext(UserContext);
  return ctx.user && ctx.user.isExternalUser;
}

export function CheckIsAdmin() {
  const ctx = useContext(UserContext);
  if (ctx && ctx.permissions) {
    const isAdmin = ctx.permissions.find(function (x) {
      return x.name == 'Administrator' || x.systemName == 'Administrator';
    });

    return !!isAdmin;
  }

  return false;
}

export function CheckIsReadOnlyUser() {
  const ctx = useContext(UserContext);
  if (ctx && ctx.permissions) {
    const isReadOnly = ctx.permissions.find(function (x) {
      return x.name == 'Roles.ReadOnly' || x.systemName == 'ReadOnly';
    });

    return !!isReadOnly;
  }

  return false;
}

export function CheckIsSuperAdmin() {
  const ctx = useContext(UserContext);
  if (ctx && ctx.permissions) {
    const isAdmin = ctx.permissions.find(function (x) {
      return x.name == 'SuperAdminClient' || x.systemName == 'SuperAdminClient';
    });

    return !!isAdmin;
  }

  return false;
}

export function CheckIsHighPrivilegeUser() {
  const ctx = useContext(UserContext);
  if (!ctx?.permissions) return false;

  const highPrivilegeRoles = [
    'SuperAdminClient',
    'SystemAdmin',
    'RegionAdmin',
    'ClientAdmin',
    'Administrator'
  ];

  return ctx.permissions.some(permission =>
    highPrivilegeRoles.includes(permission.systemName) ||
    highPrivilegeRoles.includes(permission.name)
  );
}

export function useExternalUserRedirect() {
  const ctx = useContext(UserContext);

  const isExternalUser = React.useMemo(() => {
    if (!ctx?.permissions) return false;

    const userRoles = ctx.permissions.map(role => role.systemName);
    const hasOnlyExternalRoles = userRoles.every(role =>
      role === 'ClientExternalUser' || role === 'ClientExternalUserReadOnly'
    );

    return hasOnlyExternalRoles && userRoles.length > 0;
  }, [ctx?.permissions]);

  const [redirectUrl, setRedirectUrl] = React.useState<string | null>(null);

  const getClientLink = (client: any): string => {
    if (!client || !client.region || !client.region.apiBaseUrl || !client.shortName) {
      throw new Error('Dados do cliente incompletos para redirecionamento.');
    }

    // Sanitiza valores externos
    let baseUrl = String(client.region.apiBaseUrl).trim();
    let shortName = String(client.shortName).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!shortName) throw new Error('shortName inválido para redirecionamento.');

    const ALLOWED_DOMAINS = [
      'wtwco.com',
      'willistowerswatson.com'
    ];

    let parsedBaseUrl: URL;
    try {
      parsedBaseUrl = new URL(baseUrl);
    } catch {
      throw new Error('apiBaseUrl inválido para redirecionamento.');
    }

    const isAllowedBaseDomain = ALLOWED_DOMAINS.some(domain =>
      parsedBaseUrl.hostname === domain || parsedBaseUrl.hostname.endsWith('.' + domain)
    );
    if (!isAllowedBaseDomain) {
      throw new Error('Base redirect domain not allowed');
    }

    const url = `${parsedBaseUrl.origin}/${shortName}`;

    const parsedUrl = new URL(url);
    const isAllowed = ALLOWED_DOMAINS.some(domain =>
      parsedUrl.hostname === domain || parsedUrl.hostname.endsWith('.' + domain)
    );
    if (!isAllowed) {
      throw new Error('Redirect domain not allowed');
    }

    if (!['https:', 'http:'].includes(parsedUrl.protocol)) {
      throw new Error('Protocolo de redirecionamento não permitido.');
    }

    return parsedUrl.toString();
  };

  React.useEffect(() => {
    if (isExternalUser && ctx?.user?.email) {
      const getUserClientsUrl = ENVIRONMENT.authApiPath.basePath + '/user/GetUserClients';
      fetch(`${getUserClientsUrl}?email=${encodeURIComponent(ctx.user.email)}&nameFilter=`, {
        credentials: 'include'
      })
        .then(response => {
          if (!response.ok) throw new Error('Erro ao buscar clientes do usuário.');
          return response.json();
        })
        .then(data => {
          if (data?.data && Array.isArray(data.data) && data.data.length === 1) {
            const client = data.data[0];
            try {
              const url = getClientLink(client);
              setRedirectUrl(url);
            } catch (e) {
              // Não redireciona e loga erro de segurança
              console.error('Tentativa de redirecionamento bloqueada:', e);
            }
          }
        })
        .catch(() => {
          // Não expõe detalhes sensíveis
          console.error('Erro ao buscar clientes do usuário.');
        });
    }
  }, [isExternalUser, ctx?.user?.email]);

  return { isExternalUser, redirectUrl };
}
